import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {Link, useNavigate} from 'react-router-dom';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/ui-components/Tooltip/tooltip';
import {encodeUrl, GetFileSize, TruncateTitle} from 'src/utils/helper';
import {Label} from 'src/ui-components/Label/label';
import {
    ChevronRightBlue,
    CopyLinkSmall,
    DeleteIconSmall,
    ImagePlusBlue,
    SocialShareIcon,
} from 'src/ui-components/Iconpack';
import DefaultImage from 'src/assets/icons/default-image.svg';
import {deleteMediaFile} from 'src/actions/media';
import DeleteFile from 'src/components/delete-file/deleteFile';
import {useAppDispatch} from 'src/store';
import {Button} from 'src/ui-components/Button/button';
import {Dialog, DialogContent, DialogHeader, DialogTrigger} from 'src/ui-components/Modal';
import TableListView from 'src/components/table-list-view/TableListView';
import tracker from 'src/lib/analytics/tracker';
import Tracker from 'src/lib/analytics/tracker';
import {EventNames, EventProperty, ScreenNames} from 'src/lib/analytics/events';
import SocialShare from '../social-share/SocialShare';

interface MediaWithContentProps {
    files: any[];
    orgId: string;
    onFileDelete: (fileUid: string) => void;
    onFileChildDelete: (deleteFileUid: string, childUid: string) => void;
    removeFromTable: (fileName: string) => void;
    selectAll?: boolean;
    isAllSelected?: (selected: boolean) => void;
    getSelectedData?: (selectedUids: string[]) => void;
    clearSelection?: boolean;
    resetClearState?: () => void;
}

const copyText = (text: string) => navigator.clipboard.writeText(text);

export default function MediaWithContent({
    files,
    orgId,
    onFileDelete,
    onFileChildDelete,
    removeFromTable,
    selectAll,
    isAllSelected,
    getSelectedData,
    clearSelection,
    resetClearState,
}: MediaWithContentProps) {
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteFileUid, setDeleteFileUid] = useState('');
    const [deleteFileName, setDeleteFileName] = useState('');
    const [showTransformations, setShowTransformations] = useState(false);
    const [transformedFiles, setTransformedFile] = useState<any>('');
    const [selectedFiles, setSelectedFiles] = useState<string[]>();
    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const onFileDrop = () => {
        console.log('No action');
    };

    const onChildDelete = async (deleteFileUid: string, childUid: string) => {
        const parentIndex = transformedFiles.findIndex((item: {file_uid: string}) => item.file_uid === deleteFileUid);
        const parentObject = transformedFiles[0];
        const updatedSubRows = parentObject.subRows.filter((subRow: {uid: string}) => subRow.uid !== childUid);
        transformedFiles[parentIndex] = {
            ...parentObject,
            subRows: updatedSubRows,
        };
        setTransformedFile(transformedFiles);
        onFileChildDelete(deleteFileUid, childUid);
    };

    const deleteFile = async () => {
        await dispatch(deleteMediaFile({orgUid: orgId, mediaFileUid: deleteFileUid}));
        onFileDelete(deleteFileUid);
        setDeleteFileName('');
        setDeleteFileUid('');
        setShowTransformations(false);
        setDeleteModal(false);
    };

    const deleteFileWithTransformation = async () => {
        await dispatch(deleteMediaFile({orgUid: orgId, mediaFileUid: deleteFileUid}));
        onFileDelete(deleteFileUid);
        window.location.href = 'media-library';
    };

    const suspendDeleteModal = () => {
        setDeleteModal(false);
        setDeleteFileUid('');
        setDeleteFileName('');
    };

    const onClose = () => {
        setShowTransformations(false);
    };

    const goToImageDetails = (fileUid: string) => {
        navigate(`/${orgId}/image-details/${fileUid}`);
    };

    const goToImageDetailsVersioned = (fileUid: string) => {
        navigate(`/${orgId}/image-details/${fileUid}?view=versions`);
    };

    const toggleCheckbox = (fileUid: string) => {
        setSelectedFiles((prevSelectedFiles) => {
            const isSelected = prevSelectedFiles?.includes(fileUid);
            if (isSelected) {
                return prevSelectedFiles?.filter((uid) => uid !== fileUid);
            } else {
                return [...(prevSelectedFiles || []), fileUid];
            }
        });
    };

    useEffect(() => {
        if (deleteFileUid !== '') {
            setDeleteModal(true);
        } else {
            removeFromTable(deleteFileName);
        }
    }, [deleteFileName]);

    useEffect(() => {
        if (isAllSelected) {
            isAllSelected(files.length === selectedFiles?.length);
        }
        if (getSelectedData) {
            if (selectedFiles?.length) {
                getSelectedData(selectedFiles);
            }
        }
    }, [selectedFiles]);

    useEffect(() => {
        if (selectAll) {
            const allFileUids = files.map((item: {file_uid: string}) => {
                return item.file_uid;
            });
            setSelectedFiles(allFileUids);
        } else {
            setSelectedFiles([]);
        }
    }, [selectAll]);

    useEffect(() => {
        if (clearSelection) {
            setSelectedFiles([]);
            if (resetClearState) {
                resetClearState();
            }
        }
    }, [clearSelection]);

    return (
        <div className='grid grid-cols-2 gap-2 p-2 md:grid-cols-2 md:gap-x-5 md:gap-y-6 md:p-6 lg:grid-cols-4'>
            <Dialog open={showTransformations}>
                {files.map((file, idx) => (
                    <div
                        key={idx}
                        className='border-[0.65px] border-solid border-[#D0D5DD] px-[0.472rem] py-3 pt-[0.815rem]'>
                        <div className='mb-4 h-[172px] border-[1px] border-solid border-[#EAECF0] p-3'>
                            <img
                                src={
                                    file.thumbnail_url !== ''
                                        ? file.title.toLowerCase().endsWith('.svg') ||
                                          file.title.toLowerCase().endsWith('.gif')
                                            ? encodeUrl(file.cdn_url)
                                            : encodeUrl(file.thumbnail_url)
                                        : DefaultImage
                                }
                                alt={`file-${file.uid}`}
                                className='h-full w-full cursor-pointer object-contain'
                                onClick={() => goToImageDetails(file.file_uid)}
                            />
                        </div>
                        <div className='flex flex-col'>
                            <div className='flex items-center justify-between'>
                                <span className='text-[0.875rem] font-[700] text-[#000000]'>
                                    {TruncateTitle(file.title, 20)}
                                </span>

                                <input
                                    type='checkbox'
                                    checked={selectedFiles?.includes(file.file_uid)}
                                    onChange={() => toggleCheckbox(file.file_uid)}
                                />
                            </div>
                            <span className='pt-1 text-[0.875rem] font-[400] text-[#344054]'>
                                {file.width ? `${file.width}X${file.height}` : 'NA'}
                            </span>
                            <span className='pt-1 text-[0.875rem] font-[700] text-[#344054]'>
                                {GetFileSize(file.file_size)}
                            </span>
                        </div>

                        {file.error && file.error !== '' ? (
                            <div className='flex cursor-pointer items-center justify-between gap-[0.5rem] pt-[20px]'>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Label variant='error'>{file.error.slice(0, 15)}</Label>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{file.error}</p>
                                            <TooltipArrow />
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <div
                                    className='flex justify-between'
                                    onClick={() => {
                                        setDeleteFileName(file.title);
                                    }}>
                                    <DeleteIconSmall />
                                </div>
                            </div>
                        ) : (
                            <div className='flex w-full cursor-pointer items-center justify-between pt-[20px]'>
                                {file.subRows && file.subRows.length ? (
                                    <Button
                                        className='border border-[#D1E9FF] bg-[#EFF8FF] px-[6px] py-[2px] text-[10px] font-[500] text-[#155EEF] hover:bg-[#D1E9FF] md:text-[12px]'
                                        {...{
                                            style: {cursor: 'pointer'},
                                        }}
                                        onClick={() => goToImageDetailsVersioned(file.file_uid)}>
                                        {' '}
                                        {file.subRows.length <= 1
                                            ? `+${file.subRows.length} transform`
                                            : `+${file.subRows.length} transforms`}
                                        <ChevronRightBlue />
                                    </Button>
                                ) : (
                                    <>
                                        <Link
                                            to={`/transformation/${file.file_uid}`}
                                            className='flex cursor-pointer items-center gap-[0.5rem]'>
                                            <ImagePlusBlue />
                                            <span className='text-[14px] font-[600] text-[#155EEF]'>Transform</span>
                                        </Link>
                                    </>
                                )}
                                <div className='flex justify-end'>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <Dialog>
                                                    <DialogTrigger>
                                                        <div
                                                            className='cursor-pointer pr-[12px]'
                                                            onClick={() => {
                                                                Tracker.trackEvent(EventNames.SHARE_IMAGE_CLICKED);
                                                            }}>
                                                            <SocialShareIcon height={16.67} width={15} />
                                                        </div>
                                                    </DialogTrigger>
                                                    <DialogContent className='w-[333px] md:w-[1063px]'>
                                                        <SocialShare imageUrl={file.cdn_url} />
                                                    </DialogContent>
                                                </Dialog>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Share Image</p>
                                                <TooltipArrow />
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <div
                                                    className='pr-[12px]'
                                                    onClick={() => {
                                                        copyText(encodeUrl(file.cdn_url)).then(() => {
                                                            tracker.trackEvent(EventNames.IMAGE_COPIED);
                                                            toast.success('Image Url Copied');
                                                        });
                                                    }}>
                                                    <CopyLinkSmall />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Copy Image Url</p>
                                                <TooltipArrow />
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <div
                                        onClick={() => {
                                            setDeleteFileUid(file.file_uid);
                                            setDeleteFileName(TruncateTitle(file.title, 20));
                                        }}>
                                        <DeleteIconSmall />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                <DeleteFile
                    fileName={deleteFileName}
                    onClose={suspendDeleteModal}
                    onConfirm={deleteFile}
                    open={deleteModal}
                />
                <DialogContent className='w-[300px] overflow-x-auto md:w-[1048px]' onClose={onClose}>
                    <DialogHeader>
                        <span className='text-[18px] font-[600] text-[#101828]'>
                            {' '}
                            {TruncateTitle(transformedFiles[0]?.title, 40)}
                        </span>
                    </DialogHeader>
                    <TableListView
                        files={transformedFiles}
                        onFileDrop={onFileDrop}
                        orgId={orgId}
                        onFileDelete={deleteFileWithTransformation}
                        onFileChildDelete={onChildDelete}
                        dragDiv={false}
                        removeFromTable={removeFromTable}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
